import { useCollection } from "@/features/collections/useCollection";
import { CollectionPagination } from "@/features/collections/CollectionControls";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Check, Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  createAdminSeason,
  deleteAdminSeason,
  getAdminSeasons,
  getAdminTeams,
  setDefaultAdminSeason,
  updateAdminSeason,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { unscopedKey } from "@/lib/queryClient";
import { ApiError } from "@/lib/api";
import type { Season } from "@/lib/types";
import { Badge } from "@/components/ui/Badge";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { adminTeamKeys, useCloseSeason } from "../hooks/useAdminData";
import { isArchivedTeam } from "../components/SeasonTeamGroups";

/**
 * `/admin/seasons` — global-admin season management.
 *
 * Seasons are globally shared objects: the system admin defines a
 * season once here and every team references the same list. They are
 * no longer a team sub-resource — a season created here is immediately
 * usable by all teams when they create matches.
 *
 * Delete is rejected by the backend (409) when a match still
 * references the season; the error surfaces inline in the confirm
 * dialog.
 */

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

function seasonDateRange(s: Season): string {
  if (s.starts_on && s.ends_on) return `${s.starts_on} → ${s.ends_on}`;
  if (s.starts_on) return `${s.starts_on} → ?`;
  if (s.ends_on) return `? → ${s.ends_on}`;
  return "no dates set";
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** #908: a season's team counts from the Teams list — seasons have no
 * status column, so "Closed" means every team of the season is archived. */
type SeasonTeamCounts = { total: number; active: number };
const NO_TEAMS: SeasonTeamCounts = { total: 0, active: 0 };

function seasonPeriod(
  season: Season,
  today: string,
): "past" | "current" | "upcoming" | "open" {
  if (!season.starts_on && !season.ends_on) return "open";
  if (season.ends_on && season.ends_on < today) return "past";
  if (season.starts_on && season.starts_on > today) return "upcoming";
  return "current";
}

function calendarDate(value: string): { month: string; day: string } {
  if (!value) return { month: "DATES", day: "—" };
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return { month: "DATES", day: "—" };
  return {
    month: new Intl.DateTimeFormat("en", { month: "short" })
      .format(date)
      .toUpperCase(),
    day: new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date),
  };
}

export function AdminSeasonsPage() {
  const queryClient = useQueryClient();
  const seasonsQuery = useQuery({
    queryKey: unscopedKey("admin-seasons"),
    queryFn: getAdminSeasons,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Season | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Season | null>(null);
  const [closeTarget, setCloseTarget] = useState<Season | null>(null);

  // #908: the Teams list rides along (same cache key as the Teams page) so
  // each season shows its team count and a derived "Closed" state.
  const teamsQuery = useQuery({
    queryKey: adminTeamKeys.list(),
    queryFn: getAdminTeams,
  });
  const teamCounts = useMemo(() => {
    const counts = new Map<string, SeasonTeamCounts>();
    for (const team of teamsQuery.data ?? []) {
      if (!team.season_id) continue;
      const entry = counts.get(team.season_id) ?? { total: 0, active: 0 };
      entry.total += 1;
      if (!isArchivedTeam(team)) entry.active += 1;
      counts.set(team.season_id, entry);
    }
    return counts;
  }, [teamsQuery.data]);

  const seasons = seasonsQuery.data ?? [];
  const orderedSeasons = useMemo(
    () =>
      [...seasons].sort((a, b) =>
        (b.starts_on || "").localeCompare(a.starts_on || ""),
      ),
    [seasons],
  );
  const today = todayIso();
  const collection = useCollection(orderedSeasons, {
    scope: "admin-seasons",
    text: (s) => `${s.name} ${s.starts_on} ${s.ends_on}`,
    matches: (season, value) => {
      const [period, defaultState] = value.split("|");
      return (
        (!period || seasonPeriod(season, today) === period) &&
        (!defaultState ||
          (defaultState === "default") === Boolean(season.is_default))
      );
    },
  });
  const [periodFilter = "", defaultFilter = ""] = collection.filter.split("|");
  // Refresh both the admin list AND `/api/me` — the match season picker
  // (`MatchEditModal`) reads the global `me.seasons` array, which has a
  // 5-minute stale time, so a create/edit/delete here must invalidate
  // `me` or the picker shows the old season set until the next refetch.
  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-seasons"),
    });
    void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
  };

  return (
    <div data-testid="admin-seasons" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Club setup"
        title="Season Calendar"
        description="Shared seasons keep every team’s matches in context."
        actions={
          <AdminIconButton
            icon={Plus}
            label="New season"
            visibleLabel
            size="md"
            tone="accent"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-seasons-new"
          />
        }
      />

      {seasonsQuery.error ? (
        <p
          data-testid="admin-seasons-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(seasonsQuery.error)}
        </p>
      ) : null}

      <section className="admin-season-calendar">
        <header className="admin-season-calendar-heading">
          <div>
            <p>Club calendar</p>
            <h3>Every season, in order</h3>
          </div>
          <span>
            {seasons.length} season{seasons.length === 1 ? "" : "s"}
          </span>
        </header>
        {collection.controls ? (
          <div className="admin-season-filters">
            <label className="collection-search">
              <Search aria-hidden className="collection-search-icon" />
              <span>Quick search</span>
              <input
                type="search"
                aria-label="Search seasons"
                value={collection.query}
                onChange={(event) => collection.setQuery(event.target.value)}
                placeholder="Season name or date"
                data-testid="admin-seasons-search"
                className="w-full !pl-10"
              />
            </label>
            <label>
              <span>Period</span>
              <TkticSelect
                ariaLabel="Filter seasons by period"
                value={periodFilter}
                options={[
                  { value: "", label: "All" },
                  { value: "past", label: "Past" },
                  { value: "current", label: "Current" },
                  { value: "upcoming", label: "Upcoming" },
                  { value: "open", label: "Open dates" },
                ]}
                onChange={(value) =>
                  collection.setFilter(`${value}|${defaultFilter}`)
                }
              />
            </label>
            <label>
              <span>Default</span>
              <TkticSelect
                ariaLabel="Filter seasons by default state"
                value={defaultFilter}
                options={[
                  { value: "", label: "All" },
                  { value: "default", label: "Default" },
                  { value: "other", label: "Not default" },
                ]}
                onChange={(value) =>
                  collection.setFilter(`${periodFilter}|${value}`)
                }
              />
            </label>
            <button
              type="button"
              disabled={!collection.query && !periodFilter && !defaultFilter}
              onClick={collection.clear}
            >
              Clear filters
            </button>
          </div>
        ) : null}
        {seasonsQuery.isLoading ? (
          <p className="text-xs font-mono text-ink-muted">Loading…</p>
        ) : seasons.length === 0 ? (
          <p
            data-testid="admin-seasons-empty"
            className="text-xs font-mono text-ink-muted"
          >
            No seasons yet.
          </p>
        ) : collection.items.length === 0 ? (
          <p
            data-testid="admin-seasons-empty"
            className="text-xs font-mono text-ink-muted"
          >
            No seasons match the filters.
          </p>
        ) : (
          <ul className="admin-season-list">
            {collection.items.map((s) => (
              <SeasonRow
                key={s.id}
                season={s}
                teams={teamCounts.get(s.id) ?? NO_TEAMS}
                onEdit={() => setEditTarget(s)}
                onDelete={() => setDeleteTarget(s)}
                onCloseSeason={() => setCloseTarget(s)}
                onChanged={invalidate}
              />
            ))}
          </ul>
        )}

        <CollectionPagination collection={collection} label="seasons" />
      </section>

      {createOpen ? (
        <SeasonComposer
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}
      {editTarget ? (
        <SeasonComposer
          mode="edit"
          season={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void invalidate();
          }}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteSeasonDialog
          season={deleteTarget}
          teams={teamCounts.get(deleteTarget.id) ?? NO_TEAMS}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      ) : null}
      {closeTarget ? (
        <CloseSeasonDialog
          season={closeTarget}
          teams={teamCounts.get(closeTarget.id) ?? NO_TEAMS}
          onClose={() => setCloseTarget(null)}
          onClosed={() => setCloseTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** One season row. Carries its own "Make default" mutation — pinning
 * the default is a per-row action, so the mutation can't live in the
 * parent's `.map`. */
function SeasonRow({
  season,
  teams,
  onEdit,
  onDelete,
  onCloseSeason,
  onChanged,
}: {
  season: Season;
  teams: SeasonTeamCounts;
  onEdit: () => void;
  onDelete: () => void;
  onCloseSeason: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const closed = teams.total > 0 && teams.active === 0;
  const mut = useMutation({
    mutationFn: () => setDefaultAdminSeason(season.id),
    onSuccess: () => {
      toast.success(`${season.name} is now the default season`);
      onChanged();
    },
    onError: (err) => {
      toast.error("Couldn't set default season", {
        description: formatErr(err),
      });
    },
  });
  const date = calendarDate(season.starts_on);

  return (
    <li
      data-testid="admin-season-row"
      data-season-id={season.id}
      data-default={season.is_default ? "true" : undefined}
      className="admin-season-row"
    >
      <span className="admin-season-date" aria-hidden>
        <span>{date.month}</span>
        <strong>{date.day}</strong>
      </span>
      <div className="admin-season-identity">
        <div className="flex items-center gap-1.5">
          <strong className="text-ink truncate">{season.name}</strong>
        </div>
        <div className="admin-season-range">{seasonDateRange(season)}</div>
        <div
          className="admin-season-range"
          data-testid="admin-season-teams"
          data-closed={closed ? "true" : undefined}
        >
          {teams.total === 0
            ? "No teams yet"
            : `${teams.total} team${teams.total === 1 ? "" : "s"}${
                teams.total - teams.active > 0
                  ? ` · ${teams.total - teams.active} archived`
                  : ""
              }`}
        </div>
        {closed ? (
          <Badge variant="muted" data-testid="admin-season-closed-badge">
            Closed
          </Badge>
        ) : null}
        {season.is_default ? (
          <span
            data-testid="admin-season-default-badge"
            className="admin-season-default-badge"
          >
            Default season
          </span>
        ) : null}
      </div>
      <div className="admin-season-actions">
        {season.is_default ? null : (
          <AdminIconButton
            icon={Check}
            label={
              mut.isPending ? "Setting default season" : "Make default season"
            }
            visibleLabel
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            data-testid="admin-season-set-default"
            tone="accent"
          />
        )}
        {teams.active > 0 ? (
          <AdminIconButton
            icon={Archive}
            label="Close season"
            visibleLabel
            onClick={onCloseSeason}
            data-testid="admin-season-close"
          />
        ) : null}
        <AdminIconButton
          icon={Pencil}
          label="Edit"
          aria-label="Edit season"
          visibleLabel
          onClick={onEdit}
          data-testid="admin-season-edit"
        />
        <AdminIconButton
          icon={Trash2}
          label="Delete"
          aria-label="Delete season"
          visibleLabel
          onClick={onDelete}
          data-testid="admin-season-delete"
          tone="destructive"
        />
      </div>
    </li>
  );
}

function SeasonComposer({
  mode,
  season,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  season?: Season;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(season?.name ?? "");
  const [startsOn, setStartsOn] = useState(season?.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(season?.ends_on ?? "");
  const [makeDefault, setMakeDefault] = useState(!!season?.is_default);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      mode === "create"
        ? createAdminSeason({
            name,
            starts_on: startsOn,
            ends_on: endsOn,
            is_default: makeDefault,
          })
        : updateAdminSeason(season!.id, {
            name,
            starts_on: startsOn,
            ends_on: endsOn,
            make_default: makeDefault && !season!.is_default,
          }),
    onSuccess: () => {
      toast.success(mode === "create" ? "Season created" : "Season updated");
      onSaved();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error(
        mode === "create" ? "Couldn't create season" : "Couldn't update season",
        { description: formatErr(err) },
      );
    },
  });

  return (
    <ConfirmDialog
      title={mode === "create" ? "New season" : "Edit season"}
      message={
        <div className="admin-season-dialog-form">
          <label className="flex flex-col gap-1 text-xs">
            <span>
              Season name <strong aria-hidden>*</strong>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-seasons-composer-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="admin-dialog-two-column">
            <label className="flex flex-col gap-1 text-xs">
              <span>
                Start date <strong aria-hidden>*</strong>
              </span>
              <TkticDateInput
                value={startsOn}
                onChange={setStartsOn}
                data-testid="admin-seasons-composer-starts"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span>
                End date <strong aria-hidden>*</strong>
              </span>
              <TkticDateInput
                value={endsOn}
                onChange={setEndsOn}
                data-testid="admin-seasons-composer-ends"
              />
            </label>
          </div>
          <button
            type="button"
            role="checkbox"
            aria-checked={makeDefault}
            aria-disabled={!!season?.is_default}
            className="admin-dialog-checkbox"
            onClick={() => {
              if (!season?.is_default) setMakeDefault((value) => !value);
            }}
            data-testid="admin-seasons-composer-default"
          >
            <span aria-hidden>{makeDefault ? "✓" : ""}</span>
            Use as the default season
          </button>
          <p className="admin-dialog-helper">
            New matches without an explicit season use the club default.
          </p>
          {error ? (
            <p
              data-testid="admin-seasons-composer-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={
        mut.isPending
          ? "Saving…"
          : mode === "create"
            ? "Create season"
            : "Save changes"
      }
      onConfirm={() => {
        setError(null);
        if (!name.trim()) {
          setError("Name is required.");
          return;
        }
        if (startsOn && endsOn && startsOn > endsOn) {
          setError("Start date must be on or before end date.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-composer"
    />
  );
}

function DeleteSeasonDialog({
  season,
  teams,
  onClose,
  onDeleted,
}: {
  season: Season;
  teams: SeasonTeamCounts;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => deleteAdminSeason(season.id),
    onSuccess: () => {
      toast.success("Season deleted");
      onDeleted();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't delete season", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Delete season"
      danger
      message={
        <div className="flex flex-col gap-2 text-xs">
          <p className="text-ink">
            Delete <span className="font-display">{season.name}</span>? This
            removes it for every team.
          </p>
          {teams.total > 0 ? (
            <p
              className="text-ink-muted"
              data-testid="admin-seasons-delete-teams-hint"
            >
              This season still has {teams.total} team
              {teams.total === 1 ? "" : "s"}; deleting is refused while teams
              remain. Close the season instead — its history stays readable.
            </p>
          ) : null}
          {error ? (
            <p
              data-testid="admin-seasons-delete-error"
              className="text-destructive font-mono"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Deleting…" : "Delete season"}
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-seasons-delete"
    />
  );
}

/** #908 §6 step 6 — close a season: every team of the season is archived
 * (nothing deleted, D5), which frees the club's team quota (D13). */
function CloseSeasonDialog({
  season,
  teams,
  onClose,
  onClosed,
}: {
  season: Season;
  teams: SeasonTeamCounts;
  onClose: () => void;
  onClosed: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useCloseSeason();

  return (
    <ConfirmDialog
      title="Close season"
      message={
        <div className="flex flex-col gap-2 text-xs">
          <p className="text-ink">
            Close <span className="font-display">{season.name}</span>? Archives
            the {teams.active} active team{teams.active === 1 ? "" : "s"} of
            this season. Nothing is deleted; past-season members keep their access
            to that season&rsquo;s content.
          </p>
          {error ? (
            <p
              data-testid="admin-seasons-close-error"
              className="text-destructive font-mono"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Closing…" : "Close season"}
      confirmDisabled={mut.isPending}
      confirmTestId="admin-seasons-close-confirm"
      onConfirm={() => {
        setError(null);
        mut.mutate(season.id, {
          onSuccess: (result) => {
            const archived = result.archived_team_ids?.length ?? 0;
            toast.success(`${season.name} closed`, {
              description: `${archived} team${archived === 1 ? "" : "s"} archived`,
            });
            onClosed();
          },
          onError: (err) => {
            setError(formatErr(err));
            toast.error("Couldn't close season", {
              description: formatErr(err),
            });
          },
        });
      }}
      onClose={onClose}
      data-testid="admin-seasons-close"
    />
  );
}
